import { useEffect, useRef } from "react";

interface AutoFocusNextFieldOptions {
  value: string;
  error: string | null;
  initialized?: boolean;
  expectedLength: number;
  nextFieldRef: React.RefObject<HTMLInputElement>;
}

export function useAutoFocusNextField({
  value,
  error,
  initialized = true,
  expectedLength,
  nextFieldRef,
}: AutoFocusNextFieldOptions) {
  // Track if we've already focused to prevent multiple focus attempts
  const hasFocusedRef = useRef(false);

  // Debug the initialization state
  useEffect(() => {
    console.log("AutoFocusNextField initialized state:", initialized);
  }, [initialized]);

  useEffect(() => {
    // Reset the focus tracking when value changes
    if (value.length !== expectedLength) {
      hasFocusedRef.current = false;
    }

    // Only proceed if conditions are met and we haven't already focused
    if (
      value.length === expectedLength &&
      !error &&
      !hasFocusedRef.current
      // Removed initialized check since we default to true
    ) {
      console.log("Auto-focus conditions met:", {
        value,
        expectedLength,
        error,
        nextFieldExists: !!nextFieldRef.current,
      });

      // Mark that we're attempting to focus
      hasFocusedRef.current = true;

      // Use a more direct approach with a single timeout
      const timeoutId = setTimeout(() => {
        if (nextFieldRef.current) {
          console.log("Focusing next field");
          nextFieldRef.current.focus();

          // Try to select text if available
          try {
            nextFieldRef.current.select();
          } catch (e) {
            console.log("Could not select text:", e);
          }
        }
      }, 100); // Increased timeout for reliability

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [value, error, expectedLength, nextFieldRef]);
}
